import type { EnglishTestModalProps } from './types'
import { useEnglishTestAnswerHandlers } from './useEnglishTestAnswerHandlers'
import { useEnglishTestModalEffects } from './useEnglishTestModalEffects'
import { useEnglishTestModalState } from './useEnglishTestModalState'
import { useEnglishTestSubmission } from './useEnglishTestSubmission'

type UseEnglishTestModalArgs = Pick<
  EnglishTestModalProps,
  'assessmentId' | 'defaultTest' | 'initialAnswers' | 'isOpen' | 'onComplete'
>

export function useEnglishTestModal({
  assessmentId,
  defaultTest,
  initialAnswers = [],
  isOpen,
  onComplete
}: UseEnglishTestModalArgs) {
  const {
    answers,
    answeredCount,
    currentGroupIndex,
    groupStartIndex,
    isLastGroup,
    progressPercent,
    questions,
    questionsContainerRef,
    setAnswers,
    setCurrentGroupIndex,
    setSubmissionError,
    setSubmittedAnswers,
    storedEmailValue,
    submissionError,
    submittedAnswers,
    submittedTextAnswersRef,
    totalGroups,
    totalQuestions,
    visibleQuestions
  } = useEnglishTestModalState({ defaultTest })

  const {
    flushVisibleTextAnswers,
    handleCompleteTest,
    handleSubmissionError,
    isSubmitting,
    submitMultipleChoiceAnswer,
    submitTextAnswer
  } = useEnglishTestSubmission({
    assessmentId,
    answers,
    defaultTest,
    groupStartIndex,
    onComplete,
    questions,
    setSubmissionError,
    setSubmittedAnswers,
    submittedAnswers,
    submittedTextAnswersRef,
    visibleQuestions
  })

  useEnglishTestModalEffects({
    currentGroupIndex,
    initialAnswers,
    isOpen,
    questions,
    questionsContainerRef,
    setAnswers,
    setCurrentGroupIndex,
    setSubmissionError,
    setSubmittedAnswers,
    storedEmailValue,
    submittedTextAnswersRef
  })

  const {
    handleAnswerSelect,
    handleContinue,
    handleDateAnswerChange,
    handleTextAnswerBlur,
    handleTextAnswerChange
  } = useEnglishTestAnswerHandlers({
    answers,
    flushVisibleTextAnswers,
    handleSubmissionError,
    questions,
    setAnswers,
    setCurrentGroupIndex,
    setSubmissionError,
    submitMultipleChoiceAnswer,
    submitTextAnswer
  })

  return {
    answers,
    answeredCount,
    currentGroupIndex,
    groupStartIndex,
    handleAnswerSelect,
    handleCompleteTest,
    handleContinue,
    handleDateAnswerChange,
    handleTextAnswerBlur,
    handleTextAnswerChange,
    isLastGroup,
    isSubmitting,
    progressPercent,
    questions,
    questionsContainerRef,
    setCurrentGroupIndex,
    submissionError,
    totalGroups,
    totalQuestions,
    visibleQuestions
  }
}
